import { navigate, PageProps } from 'gatsby';
import { ReactNode } from 'react';
import { useTranslation } from 'react-i18next';
import { Button, Flag, Icon, MenuButton } from '@opswat/ui';
import { useGTM } from 'hooks/useGTM';
import { PageHeader } from 'components/layout/PageHeader';
import { Footer } from 'components/footer/Footer';
import { useUser } from 'services/User';
import { canAccessPage, navigateToHomePage } from 'services/accessControl';
import { canAccessStatsPage } from 'services/statsAccessGranting';
import { Logo } from 'components/layout/Logo';
import { LoadingSpinner } from 'components/LoadingSpinner';
import { Theme, useTheme } from 'hooks/useTheme';
import { useLanguage } from 'hooks/useLanguage';
import { DashboardTabs, IntegrationsTabs } from 'services/tabs';
import { useGlobalDragDrop } from 'hooks/useGlobalDragDrop';
import { freeScanOptions } from 'hooks/useFileScanFlow';

type PublicLayoutProps = Omit<PageProps, 'children'> & { children?: ReactNode };

export const PublicLayout = ({ children, location }: PublicLayoutProps) => {
    const pathname = location?.pathname;
    const { user } = useUser();
    const { t } = useTranslation();
    const { trackClick } = useGTM();
    const { currentTheme, setTheme } = useTheme();
    const { languages, changeLanguage } = useLanguage();

    const isHomePage = pathname === '/';
    const { isDragOver, isUploading } = useGlobalDragDrop({
        enabled: isHomePage,
        fallbackOptions: freeScanOptions,
    });

    const isNavActive = (basePath: string) =>
        Boolean(pathname) && (pathname === basePath || pathname.startsWith(`${basePath}/`));

    /**
     * The two product pages behind the "Product" nav entry (MDCI-917 / MDCI-918). "Product" itself
     * only opens the dropdown, so `/product` stays reachable by URL but is not a nav destination.
     */
    const productMenuItems = [
        {
            id: 'layout-public-nav-product-enterprise',
            text: t('Enterprise'),
            description: t('Self-hosted, scalable threat prevention for your infrastructure'),
            path: '/product/enterprise',
        },
        {
            id: 'layout-public-nav-product-community',
            text: t('Community'),
            description: t('Free cloud-based file & URL analysis'),
            path: '/product/community',
        },
    ];

    const goToProductPage = (item: (typeof productMenuItems)[number]) => {
        trackClick(item.id);
        navigate(item.path);
    };

    const navButtons = [
        {
            id: 'layout-public-nav-integrations',
            text: t('Integrations'),
            basePath: '/integrations',
            onClick: () => {
                trackClick('layout-public-nav-integrations');
                navigate(`/integrations/${IntegrationsTabs.All}`);
            },
        },
        // Staff-only nav item: the same @opswat.com check WrapPage applies to the route itself.
        ...(canAccessStatsPage(user)
            ? [
                  {
                      id: 'layout-public-nav-threat-landscape',
                      text: t('Threat Share'),
                      basePath: '/explore-data',
                      onClick: () => {
                          trackClick('layout-public-nav-threat-landscape');
                          navigate('/explore-data/threat-share-report');
                      },
                  },
              ]
            : []),
        {
            id: 'layout-public-nav-blog',
            text: t('Blog'),
            basePath: '/blog',
            onClick: () => {
                trackClick('layout-public-nav-blog');
                navigate('/blog');
            },
        },
        {
            id: 'layout-public-nav-cve-vulnerabilities',
            text: t('CVE Vulnerabilities'),
            basePath: '/cve-vulnerabilities',
            onClick: () => {
                trackClick('layout-public-nav-cve-vulnerabilities');
                navigate('/cve-vulnerabilities');
            },
        },
        {
            id: 'layout-public-nav-licensing',
            text: t('Licensing'),
            basePath: '/licensing',
            onClick: () => {
                trackClick('layout-public-nav-licensing');
                navigate('/licensing');
            },
        },
        ...(canAccessPage(user, 'store')
            ? [
                  {
                      id: 'layout-public-nav-store',
                      text: t('Store'),
                      basePath: '/store',
                      onClick: () => {
                          trackClick('layout-public-nav-store');
                          navigate('/store');
                      },
                  },
              ]
            : []),
    ];

    return (
        <div
            className={`tw:font-roboto tw:w-screen tw:h-screen tw:flex tw:flex-col ${
                isDragOver || isUploading ? 'tw:opacity-50' : ''
            }`}
        >
            <header
                className='tw:relative tw:flex tw:flex-row tw:justify-between tw:items-center tw:w-full tw:h-60 tw:px-20 tw:border-b tw:border-neutrals-200 tw:dark:border-neutrals-1000'
                role='banner'
            >
                <Logo />
                <div className='tw:flex tw:flex-row tw:items-center tw:self-stretch tw:gap-20'>
                    <div className='cm-public-nav-inline tw:flex tw:flex-row tw:items-center tw:self-stretch tw:gap-20'>
                        {(() => {
                            const active = isNavActive('/product');
                            return (
                                <div className='tw:relative tw:flex tw:items-center tw:self-stretch'>
                                    <MenuButton>
                                        <MenuButton.Trigger
                                            id='layout-public-nav-product'
                                            /* `hasArrow` has no default in @opswat/ui — without it the chevron
                                               never renders and the trigger looks like a plain nav button. */
                                            hasArrow
                                            className={`tw:border-transparent tw:dark:border-transparent tw:bg-transparent tw:font-medium ${
                                                active
                                                    ? 'tw:text-blue-700 tw:dark:text-blue-400'
                                                    : 'tw:text-neutrals-800 tw:dark:text-neutrals-500 tw:hover:text-neutrals-1200 tw:dark:hover:text-white'
                                            }`}
                                        >
                                            {t('Product')}
                                        </MenuButton.Trigger>
                                        <MenuButton.Dropdown className='tw:w-xs'>
                                            {productMenuItems.map((item) => (
                                                <MenuButton.Item
                                                    key={item.path}
                                                    onClick={() => goToProductPage(item)}
                                                    /* Design marks the current page with a tinted row, not
                                                       coloured text — colouring the text also tinted the
                                                       description, which should stay neutral. */
                                                    className={
                                                        pathname === item.path
                                                            ? 'tw:bg-blue-100 tw:dark:bg-blue-1000'
                                                            : undefined
                                                    }
                                                >
                                                    <span className='tw:flex tw:flex-col tw:gap-4 tw:py-4'>
                                                        <span className='tw:font-simplon tw:font-medium tw:text-body tw:leading-none'>
                                                            {item.text}
                                                        </span>
                                                        {/* Muted via opacity rather than a text-colour pair: a
                                                            `tw:text-*` base class plus a `tw:dark:text-*` variant
                                                            loses to the unlayered @opswat/ui copy of the base, so
                                                            the description stayed dark-on-dark. Inheriting the menu
                                                            item's own colour is correct in both themes. */}
                                                        <span className='tw:text-note tw:font-normal tw:opacity-70 tw:whitespace-normal'>
                                                            {item.description}
                                                        </span>
                                                    </span>
                                                </MenuButton.Item>
                                            ))}
                                        </MenuButton.Dropdown>
                                    </MenuButton>
                                    {active && (
                                        <span
                                            aria-hidden='true'
                                            className='tw:absolute tw:inset-x-0 tw:bottom-0 tw:h-2 tw:bg-blue-700 tw:dark:bg-blue-400'
                                        />
                                    )}
                                </div>
                            );
                        })()}
                        {navButtons.map(({ basePath, ...props }) => {
                            const active = isNavActive(basePath);
                            return (
                                <div key={basePath} className='tw:relative tw:flex tw:items-center tw:self-stretch'>
                                    <Button
                                        variant='tertiary'
                                        aria-current={active ? 'page' : undefined}
                                        className={
                                            active
                                                ? 'tw:text-blue-700 tw:dark:text-blue-400'
                                                : 'tw:text-neutrals-800 tw:dark:text-neutrals-500 tw:hover:text-neutrals-1200 tw:dark:hover:text-white'
                                        }
                                        {...props}
                                    />
                                    {active && (
                                        <span
                                            aria-hidden='true'
                                            className='tw:absolute tw:inset-x-0 tw:bottom-0 tw:h-2 tw:bg-blue-700 tw:dark:bg-blue-400'
                                        />
                                    )}
                                </div>
                            );
                        })}
                    </div>
                    <span className='cm-public-nav-menu'>
                        <MenuButton>
                            <MenuButton.Trigger
                                hasArrow={false}
                                aria-label={t('Toggle navigation')}
                                className='tw:border-transparent tw:dark:border-transparent tw:p-8'
                            >
                                <Icon iconName='menu' />
                            </MenuButton.Trigger>
                            <MenuButton.Dropdown className='tw:w-220'>
                                {/* Flattened here: the collapsed menu has no room for a submenu. */}
                                {productMenuItems.map((item) => (
                                    <MenuButton.Item key={item.path} onClick={() => goToProductPage(item)}>
                                        <span
                                            className={
                                                pathname === item.path
                                                    ? 'tw:text-blue-700 tw:dark:text-blue-400'
                                                    : undefined
                                            }
                                        >
                                            {`${t('Product')} · ${item.text}`}
                                        </span>
                                    </MenuButton.Item>
                                ))}
                                {navButtons.map(({ basePath, onClick, ...rest }) => {
                                    const active = isNavActive(basePath);
                                    return (
                                        <MenuButton.Item key={basePath} onClick={onClick}>
                                            <span
                                                className={
                                                    active ? 'tw:text-blue-700 tw:dark:text-blue-400' : undefined
                                                }
                                            >
                                                {rest.text}
                                            </span>
                                        </MenuButton.Item>
                                    );
                                })}
                                <MenuButton.Separator />
                                <MenuButton.Item
                                    onClick={() =>
                                        setTheme(currentTheme.value === Theme.Dark ? Theme.Light : Theme.Dark)
                                    }
                                >
                                    <div className='tw:flex tw:items-center tw:gap-8'>
                                        <Icon iconName={currentTheme.icon} />
                                        <span>{currentTheme.label}</span>
                                    </div>
                                </MenuButton.Item>
                                {languages.map(({ code, name, flag, isSelected }) => (
                                    <MenuButton.Item key={code} onClick={() => changeLanguage(code)}>
                                        <div className='tw:flex tw:items-center tw:gap-8'>
                                            <Flag countryCode={flag ?? 'US'} />
                                            <span className='tw:flex-1'>{name}</span>
                                            {isSelected && <Icon iconName='check' />}
                                        </div>
                                    </MenuButton.Item>
                                ))}
                            </MenuButton.Dropdown>
                        </MenuButton>
                    </span>
                    {user?.isLoggedIn && (
                        <MenuButton>
                            <MenuButton.Trigger
                                onClick={() => navigate(`/dashboard/${DashboardTabs.Security}`)}
                                data-testid='public-layout-dashboard-button'
                                className='tw:border-transparent tw:dark:border-transparent tw:p-8'
                            >
                                <Icon iconName='dashboard' />
                            </MenuButton.Trigger>
                        </MenuButton>
                    )}
                    {pathname !== '/' && user?.isLoggedIn && (
                        <MenuButton>
                            <MenuButton.Trigger
                                onClick={() => navigateToHomePage(user)}
                                data-testid='public-layout-home-button'
                                className='tw:border-transparent tw:dark:border-transparent tw:p-8'
                            >
                                <Icon iconName='home' />
                            </MenuButton.Trigger>
                        </MenuButton>
                    )}
                    <PageHeader hideControlsOnMobile />
                </div>
            </header>
            {/* `relative` — see the note on SidebarLayout.Content in Layout.tsx (MDCL-18164). */}
            <main className='tw:relative tw:w-full tw:flex tw:flex-col tw:flex-1 tw:overflow-y-auto tw:overflow-x-hidden'>
                <div className='tw:flex-1'>{children}</div>
                {/* Hide the footer on /results only for logged-in users; guests keep it. */}
                {(!pathname?.startsWith('/results') || !user?.isLoggedIn) && <Footer />}
            </main>
            {isUploading && (
                <div className='tw:fixed tw:inset-0 tw:z-50 tw:flex tw:items-center tw:justify-center'>
                    <LoadingSpinner />
                </div>
            )}
        </div>
    );
};
